import { useEffect, useState, type KeyboardEvent, type ReactNode } from 'react'
import { usePreviewInlineEdit } from '@/storefront/context/PreviewInlineEditContext'

/** `max-md:text-base` avoids iOS Safari focus-zoom on small inherited typography; desktop keeps inherit. */
const inputClass =
  'w-full min-w-0 rounded-lg border border-border bg-card/95 px-2 py-1 max-md:text-base md:text-inherit shadow-sm outline-none ring-2 ring-border/30'

type As = 'span' | 'h1' | 'h2' | 'h3' | 'p' | 'div'

/**
 * Click-to-edit text in the editor preview only. Without PreviewInlineEditContext, renders static text.
 */
export function PreviewInlineText({
  propKey,
  value,
  className = '',
  as,
  multiline = false,
  placeholder = 'Click to edit…',
  tone = 'default',
  /** Shown in live storefront when `value` is empty (e.g. default title copy). */
  fallbackDisplay,
  children,
}: {
  propKey: string
  value: string
  className?: string
  /** If omitted: span (or p when multiline) */
  as?: As
  multiline?: boolean
  placeholder?: string
  /** `onDark` = light placeholder text on image/gradient heroes */
  tone?: 'default' | 'onDark'
  /** Shown in live storefront when `value` is empty (e.g. default title copy). */
  fallbackDisplay?: string
  /** Extra static content after text (rare) */
  children?: ReactNode
}) {
  const ctx = usePreviewInlineEdit()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? '')

  useEffect(() => {
    setDraft(value ?? '')
  }, [value])

  const Comp: As = as ?? (multiline ? 'p' : 'span')
  const str = typeof value === 'string' ? value : ''
  const showPlaceholder = !str.trim()
  const liveDisplay = str.trim() ? str : (fallbackDisplay ?? '')

  if (!ctx) {
    if (!liveDisplay) return null
    return (
      <Comp className={className}>
        {liveDisplay}
        {children}
      </Comp>
    )
  }

  if (editing) {
    const commit = () => {
      ctx.patch({ [propKey]: draft })
      setEditing(false)
    }
    const cancel = () => {
      setDraft(str)
      setEditing(false)
    }

    if (multiline) {
      return (
        <textarea
          data-preview-inline-editing="true"
          className={`${inputClass} ${className}`}
          rows={5}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e: KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === 'Escape') {
              e.preventDefault()
              cancel()
            }
          }}
          autoFocus
        />
      )
    }

    return (
      <input
        data-preview-inline-editing="true"
        type="text"
        className={`${inputClass} ${className}`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Escape') {
            e.preventDefault()
            cancel()
          }
          if (e.key === 'Enter') {
            e.preventDefault()
            e.currentTarget.blur()
          }
        }}
        autoFocus
      />
    )
  }

  const placeholderTone =
    tone === 'onDark' ? 'text-white/60 italic ring-1 ring-border/25' : 'text-muted-foreground italic ring-1 ring-border/60'

  return (
    <Comp
      data-preview-inline-text="true"
      className={[
        className,
        'cursor-text rounded-sm px-0.5 transition outline-none',
        showPlaceholder ? placeholderTone : '',
        'hover:ring-2 hover:ring-amber-400/60 hover:ring-offset-1 hover:ring-offset-transparent',
        'focus-visible:ring-2 focus-visible:ring-amber-500',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={(e) => {
        e.stopPropagation()
        setEditing(true)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          e.stopPropagation()
          setEditing(true)
        }
      }}
      role="button"
      tabIndex={0}
      suppressContentEditableWarning
    >
      {showPlaceholder ? placeholder : str}
      {children}
    </Comp>
  )
}
